import React, { useState, useEffect } from "react";
import { Col, FormGroup, Input, Label, Row, Badge } from "reactstrap";
import { DELIVERY_TYPES } from "../../../constants/promotionConstants";
import { GetAllUsers } from "../../../requests";

const parseCommaList = (value) =>
  value.split(",").map((item) => item.trim()).filter(Boolean);

const StepRestrictions = ({ promotion, onChange }) => {
  const restrictions = promotion.restrictions;

  // Comma-separated list fields (Specific Customers, Customer Group) need their
  // own local "raw typed text" state, decoupled from the committed array.
  // Deriving the input's value directly from restrictions.specificCustomers.join(", ")
  // re-parses (split/trim/filter-empty) on every keystroke, which strips the
  // trailing comma the instant it's typed — an empty segment after a trailing
  // comma gets filtered out, snapping the field back before the comma and making
  // it impossible to type a second entry. Committing to the parent only on blur
  // avoids that while the wizard's step-based remount (switching promotions
  // always resets to step 0) keeps this local state correctly initialized.
  const [customerGroupsText, setCustomerGroupsText] = useState(restrictions.customerGroups.join(", "));

  // Specific Customers Pill-Based state
  const [emailInput, setEmailInput] = useState("");
  const [allEmails, setAllEmails] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);

  useEffect(() => {
    const fetchEmails = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await GetAllUsers(token);
        const usersList = res?.data?.data || res?.data || [];
        const emails = usersList.map((u) => u.email).filter(Boolean);
        const uniqueEmails = Array.from(new Set(emails));
        setAllEmails(uniqueEmails);
      } catch (err) {
        console.error("Failed to load customer emails:", err);
      }
    };
    fetchEmails();
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setEmailInput(value);

    if (value.trim()) {
      const filtered = allEmails.filter(
        (email) =>
          email.toLowerCase().includes(value.toLowerCase()) &&
          !(restrictions.specificCustomers || []).includes(email)
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
      setActiveSuggestionIndex(0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (email) => {
    const currentList = restrictions.specificCustomers || [];
    if (email && !currentList.includes(email)) {
      const updated = [...currentList, email];
      onChange("specificCustomers", updated);
    }
    setEmailInput("");
    setShowSuggestions(false);
  };

  const handleRemoveCustomer = (emailToRemove) => {
    const currentList = restrictions.specificCustomers || [];
    const updated = currentList.filter((email) => email !== emailToRemove);
    onChange("specificCustomers", updated);
  };

  const handleInputKeyDown = (e) => {
    const currentList = restrictions.specificCustomers || [];
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (showSuggestions && suggestions.length > 0) {
        setActiveSuggestionIndex((prev) => (prev + 1) % suggestions.length);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (showSuggestions && suggestions.length > 0) {
        setActiveSuggestionIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (showSuggestions && activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[activeSuggestionIndex]);
      } else {
        const email = emailInput.trim();
        if (email) {
          if (!currentList.includes(email)) {
            onChange("specificCustomers", [...currentList, email]);
          }
          setEmailInput("");
          setShowSuggestions(false);
        }
      }
    } else if (e.key === ",") {
      e.preventDefault();
      const email = emailInput.trim();
      if (email) {
        if (!currentList.includes(email)) {
          onChange("specificCustomers", [...currentList, email]);
        }
        setEmailInput("");
        setShowSuggestions(false);
      }
    } else if (e.key === "Backspace" && !emailInput && currentList.length > 0) {
      const updated = currentList.slice(0, -1);
      onChange("specificCustomers", updated);
    }
  };

  const handleInputBlur = () => {
    // Small delay to let suggestion item's onMouseDown execute first
    setTimeout(() => {
      const email = emailInput.trim();
      const currentList = restrictions.specificCustomers || [];
      if (email) {
        if (!currentList.includes(email)) {
          onChange("specificCustomers", [...currentList, email]);
        }
        setEmailInput("");
      }
      setShowSuggestions(false);
    }, 200);
  };

  const handleMultiSelect = (field, event) => {
    onChange(
      field,
      Array.from(event.target.selectedOptions, (option) => option.value)
    );
  };

  return (
    <div>
      <h5 className="mb-4">Restrictions</h5>
      <Row className="mb-4">
        <Col md={6}>
          <FormGroup>
            <Label for="firstOrderOnly">First Order Only</Label>
            <Input
              type="select"
              id="firstOrderOnly"
              value={restrictions.firstOrderOnly ? "yes" : "no"}
              onChange={(e) => onChange("firstOrderOnly", e.target.value === "yes")}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </Input>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="deliveryTypes">Delivery Type</Label>
            <Input
              type="select"
              id="deliveryTypes"
              multiple
              value={restrictions.deliveryTypes}
              onChange={(e) => handleMultiSelect("deliveryTypes", e)}
            >
              {DELIVERY_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <FormGroup>
            <Label for="maximumUsage">Maximum Usage</Label>
            <Input
              id="maximumUsage"
              type="number"
              min="0"
              value={restrictions.maximumUsage}
              onChange={(e) => onChange("maximumUsage", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="maximumUsagePerCustomer">Maximum Usage Per Customer</Label>
            <Input
              id="maximumUsagePerCustomer"
              type="number"
              min="0"
              value={restrictions.maximumUsagePerCustomer}
              onChange={(e) => onChange("maximumUsagePerCustomer", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="allowStacking">Allow Stacking</Label>
            <Input
              type="select"
              id="allowStacking"
              value={restrictions.allowStacking ? "yes" : "no"}
              onChange={(e) => onChange("allowStacking", e.target.value === "yes")}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Input>
          </FormGroup>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={8}>
          <FormGroup className="position-relative">
            <Label for="specificCustomers">
              Specific Customers{" "}
              <small className="text-muted">(Select specific customer emails — leave blank to allow everyone)</small>
            </Label>
            <div
              className="form-control d-flex flex-wrap align-items-center gap-2"
              style={{
                minHeight: "38px",
                height: "auto",
                padding: "4px 8px",
                cursor: "text"
              }}
              onClick={() => document.getElementById("specificCustomersInput")?.focus()}
            >
              {(restrictions.specificCustomers || []).map((email) => (
                <Badge
                  key={email}
                  color="soft-primary"
                  className="badge-soft-primary fs-12 px-2 py-1.5 d-inline-flex align-items-center gap-1"
                  style={{ borderRadius: "4px" }}
                >
                  <span>{email}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCustomer(email);
                    }}
                    className="btn-close fs-10 p-0 ms-1"
                    style={{
                      fontSize: "8px",
                      boxShadow: "none",
                      filter: "invert(0.5)"
                    }}
                    aria-label="Remove"
                  />
                </Badge>
              ))}
              <input
                id="specificCustomersInput"
                type="text"
                value={emailInput}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                placeholder={(!restrictions.specificCustomers || restrictions.specificCustomers.length === 0) ? "Type customer email..." : ""}
                style={{
                  border: "none",
                  outline: "none",
                  flex: 1,
                  minWidth: "150px",
                  padding: 0,
                  background: "transparent",
                  fontSize: "13px"
                }}
              />
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <ul
                className="list-group position-absolute w-100 mt-1 shadow-lg"
                style={{
                  zIndex: 1000,
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #ccc"
                }}
              >
                {suggestions.map((suggestion, index) => (
                  <li
                    key={suggestion}
                    className={`list-group-item list-group-item-action py-2 px-3 ${index === activeSuggestionIndex ? "active" : ""}`}
                    style={{ cursor: "pointer" }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectSuggestion(suggestion);
                    }}
                  >
                    {suggestion}
                  </li>
                ))}
              </ul>
            )}
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="customerGroups">Customer Group</Label>
            <Input
              id="customerGroups"
              value={customerGroupsText}
              onChange={(e) => setCustomerGroupsText(e.target.value)}
              onBlur={() => onChange("customerGroups", parseCommaList(customerGroupsText))}
              placeholder="Comma-separated groups"
            />
          </FormGroup>
        </Col>
      </Row>
    </div>
  );
};

export default StepRestrictions;
